import { ReactNode } from "react";
import { Link } from "react-router";
import { mergeClass } from "../../utils/mergeClass";

export type SigneringResultVariant = "success" | "failure";

export type SigneringResultCardProps = {
  actions?: ReactNode;
  description?: ReactNode;
  icon: ReactNode;
  /**
   * Rendered via `dangerouslySetInnerHTML`. MUST be a trusted, code-owned,
   * static string (e.g. from the `messages` constants). Never pass
   * user-supplied or query/URL-derived content here — doing so is an XSS sink.
   */
  messageHtml?: string;
  title: ReactNode;
  variant: SigneringResultVariant;
};

export type SigneringResultActionLinkProps = {
  children: ReactNode;
  className?: string;
  id?: string;
  to: string;
  variant?: "primary" | "secondary";
};

export const SigneringResultActionLink = ({
  children,
  className,
  id,
  to,
  variant = "primary",
}: SigneringResultActionLinkProps) => (
  <Link
    id={id}
    to={to}
    className={mergeClass(
      "cle-button",
      "cle-button--size-lg",
      variant === "primary" && "cle-button--variant-default",
      variant === "secondary" && "cle-button--variant-outline",
      className,
    )}
  >
    {children}
  </Link>
);

const SigneringResultCard = ({
  actions,
  description,
  icon,
  messageHtml,
  title,
  variant,
}: SigneringResultCardProps) => (
  <div
    className={mergeClass(
      "cle-signering-result-card",
      `cle-signering-result-card--${variant}`,
    )}
  >
    <div className="cle-signering-result-card__hero" aria-hidden="true" />
    <div className="cle-signering-result-card__body">
      <span className="cle-signering-result-card__icon">{icon}</span>
      <h1 className="cle-signering-result-card__title">{title}</h1>
      {description ? (
        <p className="cle-signering-result-card__description">{description}</p>
      ) : null}
      {messageHtml ? (
        <div
          className="cle-signering-result-card__message"
          dangerouslySetInnerHTML={{ __html: messageHtml }}
        />
      ) : null}
      {actions ? (
        <div className="cle-signering-result-card__actions">{actions}</div>
      ) : null}
    </div>
  </div>
);

export default SigneringResultCard;
